import { produce } from 'solid-js/store';
import { mobileIsAdmin, mobileIsSuperAdmin } from '@mobileUtils/roleUtils';
import { ICustomerDetails } from '@typeDefinitions/customerTypes';

import {
  customerDetailsStore,
  setCustomerDetailsStore,
  HistoryGridPayload,
} from './store';
import {
  fetchCustomerData,
  fetchCustomerOfficeGroupsCall,
  fetchOrderHistoryData,
  updateCustomerDetailsSave,
} from './services';

export const fetchCustomerOfficeGroups = async (
  officeId: number,
  accountId?: number,
) => {
  const res = await fetchCustomerOfficeGroupsCall(officeId);

  if (res && res.isSuccess) {
    if (
      officeId ==
        (accountId ?? customerDetailsStore.customer.accountManagerOfficeId) ||
      officeId == customerDetailsStore.customer.salesManagerOfficeId
    ) {
      res.value = res.value.filter(
        (x) => x.name != 'Managers' && x.name != 'Admin',
      );
    }
    if (!mobileIsAdmin() && !mobileIsSuperAdmin()) {
      res.value = res.value.filter((x) => x.name !== 'The Office');
    }
    const assignOfficeGroupRes = res.value.map((item) => {
      return {
        ...item,
        label: `${item.name as string} (${item.office})`,
        value: item.id,
      };
    });
    setCustomerDetailsStore('assignOfficeGroup', assignOfficeGroupRes);
  }
};

export const updateCustomerDetailsState = (
  updates: Partial<ICustomerDetails>,
) => {
  Object.entries(updates).forEach(([key, value]) => {
    setCustomerDetailsStore('customer', key as keyof ICustomerDetails, value);
  });
};

const withTransportationFields = (
  customer: ICustomerDetails,
): ICustomerDetails => ({
  ...customer,
  mcNumber: customer.mcNumber ?? null,
  isAuthorityFreightBroker: customer.isAuthorityFreightBroker ?? null,
  isAuthorityCarrier: customer.isAuthorityCarrier ?? null,
  isAuthorityFreightForwarder: customer.isAuthorityFreightForwarder ?? null,
  isAuthorityDirectShipper: customer.isAuthorityDirectShipper ?? null,
  assetBased: customer.assetBased ?? null,
  isCrossBorder: customer.isCrossBorder ?? null,
});

export const getCustomerData = async (customerId: string) => {
  setCustomerDetailsStore('isLoading', true);
  const resp = await fetchCustomerData(customerId);
  if (resp) {
    setCustomerDetailsStore(
      produce((draft) => {
        draft.customer = withTransportationFields(resp);
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
    await fetchCustomerOfficeGroups(
      customerDetailsStore.customer.accountManagerOfficeId!,
    );
  } else {
    setCustomerDetailsStore(
      produce((draft) => {
        draft.isError = true;
        draft.errorMessage = 'Failed to fetch customer details';
      }),
    );
  }
  setCustomerDetailsStore('isLoading', false);
};

export const updateCustomerDetails = async (payload: ICustomerDetails) => {
  setCustomerDetailsStore('isLoading', true);
  const customerResp = await updateCustomerDetailsSave(
    withTransportationFields(payload),
  );
  if (Boolean(customerResp)) {
    setCustomerDetailsStore(
      produce((draft) => {
        draft.customer = withTransportationFields(
          customerResp as ICustomerDetails,
        );
        draft.isError = false;
        draft.errorMessage = '';
      }),
    );
  } else {
    setCustomerDetailsStore(
      produce((draft) => {
        draft.isError = true;
        draft.isLoading = false;
      }),
    );
  }
  setCustomerDetailsStore('isLoading', false);
};

export const getOrderHistory = async (customerId: string, loadMore = false) => {
  setCustomerDetailsStore('orderHistory', 'isLoading', true);

  const pageNumber = loadMore
    ? customerDetailsStore.orderHistory.pageNumber + 1
    : 1;

  const payload: HistoryGridPayload = {
    filters: [
      {
        field: 'customerId',
        filterType: 'EQUALS',
        values: [customerId],
      },
    ],
    sort: {
      field: 'pickUp',
      direction: 'desc',
    },
    pageNumber,
    pageSize: 25,
  };

  const response = await fetchOrderHistoryData(payload);

  if (response) {
    setCustomerDetailsStore(
      produce((draft) => {
        if (loadMore) {
          draft.orderHistory.data = [
            ...draft.orderHistory.data,
            ...response.data,
          ];
        } else {
          draft.orderHistory.data = response.data;
        }
        draft.orderHistory.totalCount = response.totalCount;
        draft.orderHistory.pageNumber = pageNumber;
        draft.orderHistory.hasMore = response.data.length === 25;
      }),
    );
  } else {
    setCustomerDetailsStore('orderHistory', 'hasMore', false);
  }

  setCustomerDetailsStore('orderHistory', 'isLoading', false);
};
